import React from 'react';
import { FetchResult } from '@apollo/client';

import CardArea from 'buyside/components/presentation/CardArea/cardArea';
import FilterCheckbox from 'buyside/components/presentation/Filters/FilterCheckbox/filterCheckbox';
import FilterSelect from 'buyside/components/presentation/Filters/FilterSelect/filterSelect';
import FilterTextInput from 'buyside/components/presentation/Filters/FilterTextInput/filterTextInput';
import IndexFilterChips from 'buyside/components/presentation/IndexFilterChips/IndexFilterChips';
import StatusUpdateForm from 'buyside/components/presentation/StatusUpdateForm/statusUpdateForm';

import {
  CreatePostMutationVariables,
  CreatePostMutation,
  Maybe,
  PostsIndexListDocument,
  PostsIndexListQuery,
  PostsIndexListQueryVariables,
  useCreatePostMutation
} from 'interfaces/graphql-typings';

import styles from 'buyside/components/presentation/PostIndex/postIndexStyles.scss';

const PostIndexHeader: React.FC<{
  canPost?: boolean;
  isAdmin?: boolean;
  loading?: boolean;
  profileImageUrl?: Maybe<string>;
  resultCount: string | number;
  termsAccepted?: boolean;
}> = ({ canPost, isAdmin, loading, profileImageUrl, resultCount, termsAccepted }) => {
  const [createPost, { error }] = useCreatePostMutation({
    update(cache, { data }) {
      const newPost = data?.createPost?.post;
      if (newPost) {
        cache.updateQuery<PostsIndexListQuery, PostsIndexListQueryVariables>(
          {
            query: PostsIndexListDocument,
            variables: { searchParams: '' }
          },
          data => {
            if (!data || !data?.postsIndexList?.edges || !data?.postsIndexList?.pageInfo) return;
            return {
              postsIndexList: {
                edges: [{ node: newPost }, ...data.postsIndexList.edges],
                pageInfo: data.postsIndexList.pageInfo
              },
              postsIndexListResultCount: (data.postsIndexListResultCount || 0) + 1,
              currentUser: data.currentUser
            };
          }
        );
      }
    }
  });

  return (
    <div>
      <h3>Insights</h3>
      {canPost && (
        <>
          <StatusUpdateForm
            profileImageUrl={profileImageUrl}
            onSubmit={(
              values: CreatePostMutationVariables
            ): Promise<FetchResult<CreatePostMutation>> => createPost({ variables: values })}
            termsAccepted={termsAccepted}
            initialValues={{ term1: termsAccepted, term2: termsAccepted, term3: termsAccepted }}
            isAdmin={isAdmin}
          />
          <div className={styles.error}>{error?.graphQLErrors[0]?.message}</div>
        </>
      )}
      <IndexFilterChips />
      <CardArea>
        <div className={styles.filterWrapper}>
          <FilterTextInput
            id="entities"
            placeholder="Name/Ticker Search"
            paramName="entities"
            disabled={loading}
          />

          <FilterTextInput
            id="author_name"
            placeholder="Member Search"
            paramName="author_name"
            disabled={loading}
          />

          <div className={styles.resultCount}>
            <FilterCheckbox disabled={loading} paramName="show_mine" text="Only show my posts" />
            <div className={styles.sortWrapper}>
              <span id="resultCount">{resultCount} results sorted by: </span>
              <div className={styles.sortContainer} data-heap="sort-order">
                <FilterSelect
                  disabled={loading}
                  paramName="sort_by"
                  options={[
                    { value: '', label: 'Posted Date' },
                    { value: 'comments_count', label: 'Most Commented' }
                  ]}
                  borderless
                />
              </div>
            </div>
          </div>
        </div>
      </CardArea>
    </div>
  );
};

export default PostIndexHeader;
